import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Paper, Typography, Chip, CircularProgress } from '@mui/material';
import { 
  Send as SendIcon, 
  SmartToy as BotIcon, 
  Person as UserIcon,
  AccountBalance as BillingIcon,
  Upgrade as PlanIcon,
  NetworkCheck as NetworkIcon,
  Support as SupportIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
  avatar?: string;
}

interface Message {
  id: string;
  content: string;
  isBot: boolean;
  timestamp: Date;
}

interface PrebuiltWorkflow {
  id: string;
  label: string;
  message: string;
  icon: React.ReactElement;
}

interface SimpleAIChatProps {
  user: User;
}

// Prebuilt workflows for common telecom customer service scenarios
const prebuiltWorkflows: PrebuiltWorkflow[] = [
  {
    id: 'billing',
    label: '💰 Check My Bill',
    message: 'Phone 985-542-5046 - show my current bill and balance',
    icon: <BillingIcon />
  },
  {
    id: 'plan-upgrade',
    label: '📱 Upgrade My Plan',
    message: 'I want to upgrade my current plan - what options are available?',
    icon: <PlanIcon />
  },
  {
    id: 'network-issues',
    label: '📶 Network Problems',
    message: 'My phone is not connecting to 5G network - help with troubleshooting',
    icon: <NetworkIcon />
  },
  {
    id: 'support-ticket',
    label: '🎫 Create Support Ticket',
    message: 'I need to create a technical support ticket for device troubleshooting',
    icon: <SupportIcon />
  },
  {
    id: 'account-security',
    label: '🔒 Account Security',
    message: 'I want to review my account security settings and enable two-factor authentication',
    icon: <SecurityIcon />
  }
];

const SimpleAIChat: React.FC<SimpleAIChatProps> = ({ user }) => {
  // Simple state - no complex dependencies
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(`chat_${Date.now()}_${user.id}`);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize with welcome message
  useEffect(() => {
    const welcomeMessage: Message = {
      id: 'welcome',
      content: `🌟 **Welcome to ProCogia Customer Portal**, ${user.name}!\n\nI'm your AI assistant ready to help with:\n• Account information & billing\n• Technical support & troubleshooting\n• Service plans & upgrades\n• General inquiries\n\nHow can I assist you today?`,
      isBot: true,
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
  }, []); // Only run once on mount

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle message sending
  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputMessage.trim();
    if (!text || isLoading) return;

    // Add user message immediately
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      content: text,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Call API with user context
      const response = await apiService.sendMessage(text, sessionId, user.email);
      
      // Add bot response
      const botMessage: Message = {
        id: `bot_${Date.now()}`,
        content: response.response,
        isBot: true,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        content: '❌ Sorry, I encountered an error. Please try again.',
        isBot: true,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle prebuilt workflow button clicks
  const handleWorkflowClick = (message: string) => {
    if (isLoading) return;
    handleSendMessage(message);
  };

  // Handle keyboard shortcuts
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Prebuilt workflow buttons based on user role
  const workflowButtons = user.role === 'customer' ? [
    { 
      label: '💰 Billing & Payment Inquiry', 
      message: 'Phone 985-542-5046 - show my billing history and recent charges',
      category: 'billing'
    },
    { 
      label: '📞 Network & Call Issues', 
      message: "I'm experiencing call drops and network connectivity issues in my area",
      category: 'network'
    },
    { 
      label: '📋 Account Status & Services', 
      message: 'What is my current account status and active services?',
      category: 'account'
    },
    { 
      label: '🔧 Technical Support Ticket', 
      message: 'I need technical support - my device is not connecting to 5G network properly',
      category: 'support'
    },
    { 
      label: '📈 Plan Upgrades & Features', 
      message: 'Tell me about available plan upgrades and new features',
      category: 'plans'
    },
  ] : user.role === 'agent' ? [
    { 
      label: '👤 Customer Analytics', 
      message: 'Show me customer usage analytics and insights for Marie Martin',
      category: 'analytics'
    },
    { 
      label: '🌐 Network Operations', 
      message: "What's the current network status and any incidents in the 985 area code?",
      category: 'network'
    },
    { 
      label: '🎫 Support Queue Management', 
      message: 'Show me my current support ticket queue and priority cases',
      category: 'support'
    },
    { 
      label: '💳 Billing Dispute Resolution', 
      message: 'Customer Marie Martin is disputing charges on her bill - help me investigate',
      category: 'billing'
    },
    { 
      label: '📊 Performance Metrics', 
      message: 'Show me my performance metrics and KPIs for this month',
      category: 'performance'
    },
  ] : user.role === 'admin' ? [
    { 
      label: '🏢 System Operations', 
      message: 'Show me overall system health and operational metrics',
      category: 'operations'
    },
    { 
      label: '📈 Business Analytics', 
      message: 'Provide business intelligence and revenue analytics for this quarter',
      category: 'analytics'
    },
    { 
      label: '🔧 Predictive Maintenance', 
      message: 'Are there any network towers requiring maintenance or showing degradation?',
      category: 'maintenance'
    },
    { 
      label: '👥 Agent Performance', 
      message: 'Show me department performance metrics and agent rankings',
      category: 'performance'
    },
    { 
      label: '⚠️ Service Incidents', 
      message: 'Review current service outages and incident management status',
      category: 'incidents'
    },
  ] : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '600px' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #1fb8cc 0%, #95c100 100%)', color: 'white' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <BotIcon />
          ProCogia AI Assistant
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Connected as: {user.name} ({user.role})
        </Typography>
      </Paper>

      {/* Workflow Buttons */}
      {workflowButtons.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#1e212a', fontWeight: 'bold' }}>
            🚀 Quick Actions for {user.role.charAt(0).toUpperCase() + user.role.slice(1)}:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {workflowButtons.map((button, index) => (
              <Chip
                key={index}
                label={button.label}
                onClick={() => handleSendMessage(button.message)}
                sx={{
                  backgroundColor: '#f0f9ff',
                  color: '#1fb8cc',
                  border: '2px solid #1fb8cc',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  height: '40px',
                  padding: '0 8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#1fb8cc',
                    color: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(31, 184, 204, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: '#f5f5f5',
                    color: '#999',
                    border: '2px solid #ddd',
                    cursor: 'not-allowed',
                    transform: 'none',
                    boxShadow: 'none',
                  },
                }}
                disabled={isLoading}
              />
            ))}
          </Box>
          <Typography variant="caption" sx={{ mt: 1, color: '#666', display: 'block' }}>
            💡 Click any button above to start a guided workflow, or type your own question below
          </Typography>
        </Box>
      )}

      {/* Messages Area */}
      <Paper 
        sx={{ 
          flexGrow: 1, 
          p: 2, 
          mb: 2, 
          maxHeight: '400px', 
          overflowY: 'auto',
          backgroundColor: '#fafafa'
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              mb: 2,
              flexDirection: message.isBot ? 'row' : 'row-reverse',
            }}
          >
            {/* Avatar */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: message.isBot ? '#1fb8cc' : '#95c100',
                color: 'white',
                mx: 1,
                flexShrink: 0,
              }}
            >
              {message.isBot ? <BotIcon fontSize="small" /> : <UserIcon fontSize="small" />}
            </Box>

            {/* Message Bubble */}
            <Paper
              sx={{
                p: 2,
                maxWidth: '70%',
                backgroundColor: message.isBot ? 'white' : '#e3f2fd',
                border: message.isBot ? '1px solid #e0e0e0' : '1px solid #1fb8cc',
              }}
            >
              <Typography
                variant="body1"
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {message.content}
              </Typography>
              <Typography
                variant="caption"
                sx={{ 
                  color: '#666', 
                  display: 'block', 
                  mt: 1,
                  fontSize: '0.75rem'
                }}
              >
                {message.timestamp.toLocaleTimeString()}
              </Typography>
            </Paper>
          </Box>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#1fb8cc',
                color: 'white',
                mx: 1,
              }}
            >
              <BotIcon fontSize="small" />
            </Box>
            <Paper sx={{ p: 2, backgroundColor: 'white', border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  AI is thinking...
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Paper>

      {/* Prebuilt Workflows */}
      <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1.5, 
            color: '#1e212a',
            fontWeight: 600,
            fontSize: '0.875rem'
          }}
        >
          🚀 Quick Actions - Click to Get Started:
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1,
          justifyContent: 'flex-start'
        }}>
          {prebuiltWorkflows.map((workflow) => (
            <Chip
              key={workflow.id}
              label={workflow.label}
              icon={workflow.icon}
              onClick={() => handleSendMessage(workflow.message)}
              disabled={isLoading}
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #1fb8cc',
                color: '#1e212a',
                fontSize: '0.8rem',
                height: '36px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#1fb8cc',
                  color: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(31, 184, 204, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#f0f0f0',
                  color: '#999',
                  border: '1px solid #e0e0e0',
                },
                '& .MuiChip-icon': {
                  color: 'inherit',
                  fontSize: '1rem',
                },
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* Input Area */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={3}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message here..."
            disabled={isLoading}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
              },
            }}
          />
          <IconButton
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            sx={{
              backgroundColor: '#95c100',
              color: 'white',
              '&:hover': {
                backgroundColor: '#7ea000',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999',
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default SimpleAIChat; 